import Link from 'next/link';
import throttle from 'lodash/throttle';
import { CaretUp } from '@phosphor-icons/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { NavigationHighlightLinkGroup } from '@tmh-website/shared/cms';
import ButtonIcon from '@tmh-website/ui/ButtonIcon';
import MenuIcon from '@tmh-website/components/icons/phosphor-extension/icons/Menu';
import { Color } from '@tmh-website/shared/colors';
import { filterNavigationHighlightsByAudience } from '@tmh-website/services/cms.service';
import { GlobalContext, PageContext, ProjectContext, ScrollContext, useHomepageLink } from '@tmh-website/shared';
import { hasAudiences, hasLocales, LocalePrefix } from '@tmh-website/shared/const';
import { isNonEmptyString } from '@tmh-website/shared/helpers';
import { isProjectWithSearch } from '@tmh-website/services/search.client.service';
import { useLogoComponent } from '@tmh-website/shared/hooks';
import {
  isCMSPage,
  isKnowledgeCenterWebinarPage,
  isKnowledgeCenterWhitepaperPage,
} from '@tmh-website/services/page.service';

// TODO: replace useDeprecatedSearch
import { useDeprecatedSearch } from '@tmh-website/services/search.service';

import type { LogoWrapperProps } from './Header.styles';
import * as Styled from './Header.styles';
import HeaderButtons from './HeaderButtons';
import LocaleSwitchBanner from './LocaleSwitchBanner';
import Menu from './Menu';
import Navigation from './Navigation';
import PreviewBanner from './PreviewBanner';
import Search from './Search';
import SearchDeprecated from './Search/SearchDeprecated';
import SubNavigations from './SubNavigations';
import { getLogoWrapperHeight } from './Header.helpers';
import { HeaderThemeColor, headerThemes } from './Header.theme';

export interface HeaderProps {
  locale: LocalePrefix;
  currentPath: string;

  /**
   * Enable this flag when this component is used outside of the Next.js context.
   */
  isStandaloneMode?: boolean;
}

const Header = ({ locale, currentPath, isStandaloneMode }: HeaderProps) => {
  const page = useContext(PageContext);
  const {
    audience,
    audienceIsLoaded,
    customLogo,
    headerTheme: globalHeaderTheme,
    isHeaderHamburgerButtonVisible = true,
    isPreview,
    navigationHighlights,
  } = useContext(GlobalContext);
  const { t } = useTranslation();
  const { project } = useContext(ProjectContext);
  const { localeSwitchBannerIsVisible, setHeaderIsVisible } = useContext(ScrollContext);

  const [menuIsVisible, setMenuIsVisible] = useState(false);
  const [visibleSubnav, setVisibleSubnav] = useState<number>();
  const subnavVisiblityTimeout = useRef<number>();

  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const topOffset = 20;
  const [isOnTop, setIsOnTop] = useState(true);
  const [isBelowFold, setIsBelowFold] = useState(false);
  const headerIsHidden = isScrollingDown && !isOnTop;
  const envHasAudiences = hasAudiences(locale, project);
  const homepageLink = useHomepageLink({ locale });
  const LogoComponent = useLogoComponent();

  const shouldRenderSearch: boolean = isProjectWithSearch(project);

  /**
   * Show/hide header
   */
  useEffect(() => {
    let lastScrollY = window.scrollY;
    function onScroll() {
      const scrollY = window.scrollY;
      setIsOnTop(scrollY < topOffset);
      setIsBelowFold(scrollY > window.innerHeight);
      setIsScrollingDown(scrollY >= lastScrollY);
      lastScrollY = scrollY;
    }
    const throttledOnScroll = throttle(onScroll, 100);
    window.addEventListener('scroll', throttledOnScroll);
    return () => window.removeEventListener('scroll', throttledOnScroll);
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() => setHeaderIsVisible(!headerIsHidden));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerIsHidden]);

  if (!page) {
    return null;
  }

  /**
   * Get header theme
   */
  let headerTheme: HeaderThemeColor = globalHeaderTheme || Color.Neutral_0_White;

  if (isCMSPage(page)) {
    if (isBelowFold && isNonEmptyString(page.headerThemeBelowFold)) {
      headerTheme = page.headerThemeBelowFold;
    } else if (isNonEmptyString(page.headerTheme)) {
      headerTheme = page.headerTheme as HeaderThemeColor;
    }
  } else if (isKnowledgeCenterWebinarPage(page) || isKnowledgeCenterWhitepaperPage(page)) {
    headerTheme = Color.Blue_1000_Deep;
  }

  /**
   * Get visible highlights navigation entries
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const visibleHighlightLinkGroups = useMemo<NavigationHighlightLinkGroup[]>(() => {
    if (!hasAudiences(locale, project)) {
      return navigationHighlights ?? [];
    }

    if (!audience || !navigationHighlights) {
      return [];
    }

    return filterNavigationHighlightsByAudience(navigationHighlights, audience);
  }, [audience, locale, navigationHighlights]);

  function showSubnav(index: number) {
    clearTimeout(subnavVisiblityTimeout.current);
    subnavVisiblityTimeout.current = undefined;
    setVisibleSubnav(index);
  }

  function hideSubnav(force = false) {
    if (force) {
      clearTimeout(subnavVisiblityTimeout.current);
      setVisibleSubnav(undefined);
      return;
    } else if (!subnavVisiblityTimeout.current) {
      subnavVisiblityTimeout.current = window.setTimeout(() => setVisibleSubnav(undefined), 500);
    }
  }

  // Used to hide audiences in cart
  const isCartPage: boolean =
    typeof window !== 'undefined' &&
    typeof Object.values(LocalePrefix).find(locale =>
      window.location.pathname.startsWith(`/${locale}/checkout/cart`)
    ) !== 'undefined';

  const hasCustomLogo: boolean = typeof customLogo !== 'undefined';

  const logoWrapperHeight: LogoWrapperProps['$height'] = getLogoWrapperHeight(project, hasCustomLogo);

  return (
    <>
      {hasLocales(project) && (
        <LocaleSwitchBanner locale={locale} themeColor={headerThemes[headerTheme].localeSwitchBanner} />
      )}
      <Styled.GlobalHeader
        $themeColor={headerTheme}
        $isTransparent={isOnTop}
        $isHidden={headerIsHidden}
        $localeSwitchBannerIsVisible={localeSwitchBannerIsVisible}
        data-cy='header'>
        {!!isPreview && <PreviewBanner exp={isPreview.exp} />}
        {envHasAudiences && !isCartPage && (
          <Styled.AdjustedAudienceTabs
            locale={locale}
            currentPath={currentPath}
            headerThemeColor={headerTheme}
            isStandaloneMode={!!isStandaloneMode}
          />
        )}
        <Styled.Container>
          <Link passHref href={homepageLink ?? '/'} locale={locale} legacyBehavior>
            <Styled.LogoWrapper aria-label={t('global:menu.backToHome') as string} $height={logoWrapperHeight}>
              {hasCustomLogo ? (
                customLogo === 'hidden' ? null : (
                  <Styled.CustomLogo src={customLogo} />
                )
              ) : (
                <LogoComponent />
              )}
            </Styled.LogoWrapper>
          </Link>

          {visibleHighlightLinkGroups.length > 0 && (
            <Navigation
              locale={locale}
              isHidden={!audienceIsLoaded}
              isStandaloneMode={!!isStandaloneMode}
              showSubnav={showSubnav}
              hideSubnav={hideSubnav}
              headerTheme={headerTheme}
              visibleHighlightLinkGroups={visibleHighlightLinkGroups}
            />
          )}

          <Styled.RightContainer>
            <HeaderButtons
              isStandaloneMode={!!isStandaloneMode}
              buttonsThemeColor={headerThemes[headerTheme].buttonThemeColor}
              dropdownsThemeColor={Color.Neutral_200_OffWhite}
              locale={locale}
              audience={audience}
              audienceIsLoaded={audienceIsLoaded}
              outlineButtonThemeColor={headerThemes[headerTheme].outlineButtonThemeColor}
            />

            {/* "Hamburger" button */}
            {isHeaderHamburgerButtonVisible && (
              <ButtonIcon
                themeColor={headerThemes[headerTheme].buttonThemeColor}
                onClick={() => setMenuIsVisible(!menuIsVisible)}
                isSmall={true}
                data-cy='openMenuButton'
                aria-label={t('global:menu.openMenu') as string}>
                <MenuIcon size={40} isSmall />
              </ButtonIcon>
            )}
            {/* END | "Hamburger" button */}
          </Styled.RightContainer>
        </Styled.Container>
      </Styled.GlobalHeader>

      {visibleHighlightLinkGroups.length > 0 && (
        <SubNavigations
          isStandaloneMode={!!isStandaloneMode}
          headerTheme={headerTheme}
          showSubnav={showSubnav}
          hideSubnav={hideSubnav}
          visibleSubnav={visibleSubnav}
          visibleHighlightLinkGroups={visibleHighlightLinkGroups}
          locale={locale}
        />
      )}

      <Styled.BackToTopBtn
        themeColor={Color.Blue_1000_Deep}
        href='#'
        $isVisible={isScrollingDown}
        aria-label={t('global:menu.backToTop') as string}>
        <CaretUp size={20} weight='bold' />
      </Styled.BackToTopBtn>

      <Menu
        menuIsVisible={menuIsVisible}
        setMenuIsVisible={setMenuIsVisible}
        locale={locale}
        page={page}
        currentPath={currentPath}
        isStandaloneMode={!!isStandaloneMode}
      />

      {shouldRenderSearch &&
        (useDeprecatedSearch ? (
          <SearchDeprecated locale={locale} isStandaloneMode={isStandaloneMode} />
        ) : (
          <Search locale={locale} isStandaloneMode={isStandaloneMode} />
        ))}
    </>
  );
};

export default Header;
